import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FileText, Briefcase, MessageSquare, Settings, LogOut, Menu, X, ExternalLink, Home, Layers, Building2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ADMIN } from '@/constants/testIds';

const NAV_GROUPS = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', href: '/admin', icon: LayoutDashboard, exact: true },
      { label: 'Inquiries', href: '/admin/inquiries', icon: MessageSquare },
    ]
  },
  {
    label: 'Content',
    items: [
      { label: 'Blog & Insights', href: '/admin/blog', icon: FileText },
      { label: 'Case Studies', href: '/admin/cases', icon: Briefcase },
      { label: 'Services', href: '/admin/services', icon: Layers },
      { label: 'Industries', href: '/admin/industries', icon: Building2 },
    ]
  },
  {
    label: 'Settings',
    items: [
      { label: 'Homepage', href: '/admin/homepage', icon: Home },
      { label: 'Website Settings', href: '/admin/settings', icon: Settings },
    ]
  },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const isActive = (item) => item.exact ? location.pathname === item.href : location.pathname.startsWith(item.href);

  const allItems = NAV_GROUPS.flatMap(g => g.items);

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6 border-b border-[#E5E7EB]">
        <Link to="/" className="flex items-center gap-0">
          <span className="font-serif font-bold text-[1.1rem] text-[#153E5C] tracking-tight">NKP</span>
          <span className="ml-2 font-mono text-[0.55rem] tracking-[0.32em] uppercase font-medium text-[#0E5A57]">VENTURES</span>
        </Link>
        <p className="text-xs text-[#9CA3AF] font-mono mt-1">Admin Panel</p>
      </div>

      {/* Nav */}
      <nav className="flex-1 p-4 space-y-5 overflow-y-auto" data-testid={ADMIN.sidebar}>
        {NAV_GROUPS.map(group => (
          <div key={group.label}>
            <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-widest px-3 mb-1.5 font-sans">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link key={item.href} to={item.href} onClick={() => setSidebarOpen(false)}
                    className={`admin-sidebar-link ${active ? 'bg-teal-50 text-teal-700 font-medium' : 'text-[#6B7280]'}`}>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {item.label}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-[#E5E7EB]">
        <a href="/" target="_blank" rel="noopener noreferrer"
          className="admin-sidebar-link text-[#6B7280] mb-1">
          <ExternalLink className="w-4 h-4" /> View Website
        </a>
        <button
          data-testid={ADMIN.logoutBtn}
          onClick={handleLogout}
          className="admin-sidebar-link w-full text-left text-red-500 hover:bg-red-50"
        >
          <LogOut className="w-4 h-4" /> Sign Out
        </button>
        {user && (
          <div className="mt-3 px-3 py-2 bg-[#F7FAFC] rounded-lg">
            <p className="text-xs font-medium text-[#374151] font-sans truncate">{user.email}</p>
            <p className="text-xs text-[#9CA3AF] font-mono capitalize">{user.role}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#F7FAFC]" data-testid={ADMIN.dashboard}>
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex w-60 flex-col bg-white border-r border-[#E5E7EB] flex-shrink-0">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/30" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-60 bg-white border-r border-[#E5E7EB] z-10">
            <SidebarContent />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <header className="bg-white border-b border-[#E5E7EB] px-6 py-4 flex items-center justify-between flex-shrink-0">
          <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 rounded-lg text-[#6B7280] hover:bg-[#F3F4F6]">
            <Menu className="w-5 h-5" />
          </button>
          <div className="hidden lg:block">
            <h1 className="text-sm font-semibold text-[#374151] font-sans">
              {allItems.find(n => isActive(n))?.label || 'Admin'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#9CA3AF] font-sans hidden sm:block">Welcome, {user?.name || 'Admin'}</span>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
